import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { BeatLoader } from 'react-spinners';
import { getRegistrationDetails, getRegistrationDetailByActivityIdAndPhoneNumber } from '../api/registration';
import { decodeTokenToPhoneNumber, isValidToken } from '../utils/tokenUtils';
import { getFanMemberBySlug, fanPath, getFanMemberBrandTheme } from '../config/fanMembersConfig';

const FanMemberProtectedRoute = ({ fanSlug, children }) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const fanMember = getFanMemberBySlug(fanSlug);
    const brand = getFanMemberBrandTheme(fanMember);

    useEffect(() => {
        const checkAuth = async () => {
            if (!fanMember) {
                setIsAuthenticated(false);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const token = searchParams.get('token');

                if (token) {
                    if (isValidToken(token)) {
                        const phoneNumber = decodeTokenToPhoneNumber(token);

                        if (phoneNumber) {
                            try {
                                const response = await getRegistrationDetailByActivityIdAndPhoneNumber(
                                    fanMember.activityId,
                                    phoneNumber,
                                );
                                if (response.success && response.data.isVerified) {
                                    Cookies.set('registrationId', response.data.id, { expires: 1 });
                                    Cookies.set('phoneNumber', phoneNumber, { expires: 1 });
                                    setIsAuthenticated(true);
                                } else {
                                    Cookies.remove('registrationId');
                                    Cookies.remove('phoneNumber');
                                    setIsAuthenticated(false);
                                }
                            } catch (apiError) {
                                console.error('API verification error for token-based access:', apiError);
                                Cookies.remove('registrationId');
                                Cookies.remove('phoneNumber');
                                setIsAuthenticated(false);
                            }
                            setIsLoading(false);
                            return;
                        }
                    }

                    Cookies.remove('registrationId');
                    Cookies.remove('phoneNumber');
                    setIsAuthenticated(false);
                    setIsLoading(false);
                    return;
                }

                const registrationId = Cookies.get('registrationId');
                const phoneNumber = Cookies.get('phoneNumber');

                if (!registrationId || !phoneNumber) {
                    setIsAuthenticated(false);
                    setIsLoading(false);
                    return;
                }

                try {
                    const response = await getRegistrationDetails(registrationId, fanMember.activityId);
                    if (response.success && response.data.isVerified) {
                        setIsAuthenticated(true);
                    } else {
                        Cookies.remove('registrationId');
                        Cookies.remove('phoneNumber');
                        setIsAuthenticated(false);
                    }
                } catch (apiError) {
                    console.error('API verification error:', apiError);
                    Cookies.remove('registrationId');
                    Cookies.remove('phoneNumber');
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Authentication check error:', error);
                Cookies.remove('registrationId');
                Cookies.remove('phoneNumber');
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [searchParams, fanMember]);

    if (isLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${brand.pageBgDark}`}>
                <div className="text-center">
                    <BeatLoader color={brand.loaderDark} size={15} />
                    <p className="mt-4 text-sm text-slate-300 font-medium">
                        Checking authentication...
                    </p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to={fanPath(fanSlug, 'login')} state={{ from: location }} replace />;
    }

    return children;
};

export default FanMemberProtectedRoute;
